"use client";
import { useState, useEffect, useRef } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { CreditCard, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { REST_API_URL } from "../api/config";
import { useAppSelector } from "../hooks/redux/useRedux";
import { selectCheckPayment } from "../redux/slices/paymentSlice/PaymentSlice";

const API_URL = REST_API_URL;
const STORY_DURATION = 5000;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token: string | null, additionalHeaders: Record<string, string> = {}) => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

async function authFetch(url: string, options: RequestInit = {}) {
  let access =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");
  let refresh =
    localStorage.getItem("refreshToken") ||
    sessionStorage.getItem("refreshToken");

  const doRequest = async (token: string | null) => {
    const extra = (options.headers instanceof Headers || Array.isArray(options.headers))
      ? {}
      : (options.headers as Record<string, string> | undefined) ?? {};
    const headers = buildHeaders(token, extra);

    return fetch(url, {
      ...options,
      headers,
    });
  };

  if (!access) return { status: 401, ok: false, json: async () => ({}) };

  let response = await doRequest(access);
  if (response.status !== 401) return response;

  if (!refresh) return response;

  const refreshHeaders = buildHeaders(null);

  const refreshRes = await fetch(`${API_URL}/auth/token/refresh/`, {
    method: "POST",
    headers: refreshHeaders,
    body: JSON.stringify({ refresh }),
  });

  const refreshData = await refreshRes.json().catch(() => ({}));

  if (refreshRes.ok && refreshData.access) {
    access = refreshData.access;
    localStorage.setItem("accessToken", access);
    return doRequest(access);
  }

  localStorage.clear();
  sessionStorage.clear();
  return response;
}

export default function NewsUpdates() {
  const [news, setNews] = useState([]);
  const [social, setSocial] = useState(null);

  const [showSubModal, setShowSubModal] = useState(false);

  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [progress, setProgress] = useState(0);
  const paymentCheck = useAppSelector(selectCheckPayment);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  const instagramUrl = (email) =>
    `https://www.instagram.com/oauth/authorize?enable_fb_login=0&force_authentication=1&disable_native=1&client_id=1362107724965082&redirect_uri=${API_URL}/auth/connect-instagram&response_type=code&scope=instagram_business_basic,instagram_business_manage_messages&state=${encodeURIComponent(
      email,
    )}|||web`;

  const whatsappUrl = (email) =>
    `https://www.facebook.com/v23.0/dialog/oauth?display=popup&client_id=1167918065008974&redirect_uri=${encodeURIComponent(
      "https://testing.halper.ai/api/whatsapp/connect-whatsapp/",
    )}&config_id=1412479926475322&response_type=code&auth_type=&fallback_redirect_uri=${encodeURIComponent(
      "https://testing.halper.ai/api/whatsapp/connect-whatsapp/",
    )}&override_default_response_type=true&extras=%7B%22featureType%22%3A%22whatsapp_business_app_onboarding%22%2C%22sessionInfoVersion%22%3A%223%22%2C%22version%22%3A%22v3%22%2C%22features%22%3A%5B%7B%22name%22%3A%22marketing_messages_lite%22%7D%2C%7B%22name%22%3A%22app_only_install%22%7D%5D%7D&state=${encodeURIComponent(
      email + "|||web",
    )}`;

  const nextSlide = () =>
    setIndex((prev) => (prev + 2 < news.length ? prev + 2 : 0));

  const prevSlide = () =>
    setIndex((prev) => (prev - 2 >= 0 ? prev - 2 : news.length - 2));

  useEffect(() => {
    const load = async () => {
      const [res] = await Promise.all([
        authFetch(`${API_URL}/auth/social-profile-view/`),
      ]);
      if (res.ok) {
        setSocial(await res.json());
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!social) return;

    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");

    if (!token) {
      navigate("/sign-in");
      return;
    }

    const loadStories = async () => {
      try {
        setLoading(true);

        const res = await authFetch(`${API_URL}/story/stories/`);
        if (res.status === 401) {
          navigate("/sign-in");
          return;
        }

        const data = await res.json();
        if (!Array.isArray(data)) {
          setNews([]);
          return;
        }

        const email =
          social?.email ||
          localStorage.getItem("pendingEmail") ||
          "user@halper.ai";

        const formatted = [];

        data.forEach((block, i) => {
          const items = Array.isArray(block.items) ? block.items : [];

          if (block.base_navigation === "instagram" && social.instagram) return;
          if (block.base_navigation === "whatsapp" && social.whatsapp) return;

          if (items.length > 0) {
            items.forEach((itm, idx) => {
              formatted.push({
                id: `${block.id}-${idx}`,
                img:
                  itm.background_image || itm.image || block.icon_image || "",
                title: itm.title || block.title || "Update",
                desc: itm.description || block.description || null,
                button_text: itm.button_text || block.button_text || null,
                requiresSubscription:
                  block.base_navigation === "instagram" ||
                  block.base_navigation === "whatsapp",
                action: () => {
                  if (itm.website_navigation)
                    window.open(itm.website_navigation, "_blank");
                  else if (block.website_navigation)
                    window.open(block.website_navigation, "_blank");
                  else if (block.base_navigation === "instagram")
                    window.open(instagramUrl(email), "_blank");
                  else if (block.base_navigation === "whatsapp")
                    window.open(whatsappUrl(email), "_blank");
                },
              });
            });
          } else {
            formatted.push({
              id: block.id ?? i,
              img: block.icon_image || "",
              title: block.title || "Update",
              desc: block.description || null,
              button_text: block.button_text || null,
              requiresSubscription:
                block.base_navigation === "instagram" ||
                block.base_navigation === "whatsapp",
              action: () => {
                if (block.website_navigation)
                  window.open(block.website_navigation, "_blank");
                else if (block.base_navigation === "instagram")
                  window.open(instagramUrl(email), "_blank");
                else if (block.base_navigation === "whatsapp")
                  window.open(whatsappUrl(email), "_blank");
              },
            });
          }
        });

        setNews(formatted);
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    };

    loadStories();
  }, [social, navigate]);

  useEffect(() => {
    if (selectedIndex === null || showSubModal) {
      clearInterval(timerRef.current);
      return;
    }

    setProgress(0);
    const start = Date.now();

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min((elapsed / STORY_DURATION) * 100, 100);
      setProgress(pct);

      if (elapsed >= STORY_DURATION) {
        clearInterval(timerRef.current);

        setSelectedIndex((prev) => {
          if (prev === news.length - 1) return null;
          return prev + 1;
        });
      }
    }, 50);

    return () => clearInterval(timerRef.current);
  }, [selectedIndex, news.length, showSubModal]);

  const currentStory = selectedIndex !== null ? news[selectedIndex] : null;

  return (
    <div className="rounded-xl bg-white shadow p-6 w-full h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-neutral-800">News & Updates</h2>

        <div className="flex gap-4">
          <button
            onClick={prevSlide}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-[#5563F6]"
          >
            <ChevronLeftIcon className="w-5 h-5 text-white" />
          </button>

          <button
            onClick={nextSlide}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-[#5563F6]"
          >
            <ChevronRightIcon className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      <div className="flex-grow min-h-0">
        {loading ? (
          <div className="flex items-center justify-center h-full text-neutral-400">
            Loading...
          </div>
        ) : news.length === 0 ? (
          <div className="text-center text-neutral-400 py-10">
            No news available
          </div>
        ) : (
          <div className="overflow-hidden relative h-full">
            <motion.div
              className="flex h-full"
              animate={{ x: `-${(index / 2) * 100}%` }}
              transition={{ type: "spring", stiffness: 120, damping: 20 }}
            >
              {news.map((item, idx) => (
                <div key={item.id} className="w-1/2 flex-shrink-0 px-2 h-full">
                  <div
                    onClick={() => setSelectedIndex(idx)}
                    className="rounded-xl overflow-hidden shadow h-full cursor-pointer group relative"
                  >
                    <img
                      src={item.img}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                    />

                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
                      <h3 className="text-white text-sm font-semibold">
                        {item.title}
                      </h3>
                      {item.desc && (
                        <p className="text-white/80 text-xs mt-1 line-clamp-2">
                          {item.desc}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {currentStory && (
          <motion.div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute top-0 left-0 right-0 flex gap-1 p-3">
              {news.map((_, i) => (
                <div
                  key={i}
                  className="h-1 flex-1 bg-white/30 rounded-full overflow-hidden"
                >
                  <motion.div
                    className="h-1 bg-white"
                    animate={{
                      width:
                        i < selectedIndex
                          ? "100%"
                          : i === selectedIndex
                            ? `${progress}%`
                            : "0%",
                    }}
                  />
                </div>
              ))}
            </div>

            <button
              onClick={() => setSelectedIndex(null)}
              className="absolute top-5 right-5 bg-white/20 hover:bg-white/40 rounded-full p-2"
            >
              <XMarkIcon className="w-6 h-6 text-white" />
            </button>

            <motion.img
              key={currentStory.img}
              src={currentStory.img}
              alt={currentStory.title}
              className="max-h-[80vh] max-w-[90vw] object-contain rounded-xl shadow-lg"
            />

            <div className="text-center text-white mt-4 px-6 w-[400px]">
              <h3 className="text-lg font-semibold">{currentStory.title}</h3>

              {currentStory.desc && (
                <p className="text-white/80 text-sm mt-2 whitespace-pre-line">
                  {currentStory.desc}
                </p>
              )}

              {currentStory.button_text && (
                <button
                  onClick={() => {
                    if (
                      currentStory.requiresSubscription &&
                      !paymentCheck?.has_active_subscription
                    ) {
                      setShowSubModal(true);
                      return;
                    }
                    currentStory.action();
                  }}
                  className="mt-4 bg-white text-black px-4 py-2 rounded-full"
                >
                  {currentStory.button_text}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSubModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => setShowSubModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-2xl shadow-xl w-[340px] p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowSubModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
              >
                <X size={18} />
              </button>

              <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-100 mb-4">
                <CreditCard size={22} className="text-amber-500" />
              </div>

              <h3 className="text-[16px] font-bold text-[#302B2B] mb-2">
                Subscription Required
              </h3>
              <p className="text-sm text-gray-500 mb-5">
                You need an active subscription to connect channels.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowSubModal(false)}
                  className="flex-1 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowSubModal(false);
                    navigate("/profile?section=Settings&tab=subscription");
                  }}
                  className="flex-1 py-2 rounded-xl bg-gradient-to-r from-[#5563F6] to-[#8B54FF] text-white text-sm font-medium hover:opacity-90 transition"
                >
                  View Plans
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
